import { BiobakeryPipeline, ChannelName } from '@/enums';
import InputsMapper from '@/form/components/InputsMapper';
import { OmitControlControlledInputProps } from '@/form/components/RHFControlledInput';
import useRHFForm from '@/form/hooks/useRHFForm';
import {
  handleFilterOption,
  handleScrollPopupToGetNextPage,
  handleSearchSelect,
  transformInfiniteQueryToSelectOptions,
} from '@/form/utils';
import { useGetChannels } from '@/hooks';
import { AnalysisBatchCreateDto, ProjectEntity } from '@/interfaces';
import {
  useCreateCommercialAnalysisBatch,
  useGetInfiniteProjects,
} from '@/queries';
import { CustomButton, CustomModal } from '@amili-v3/amili-v3-design';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { createCommercialAnalysisSchema } from '../../schemas';
import { performCommercialVisibleChannels } from '@/constants';

const parseS3UriWithURL = (uri: string) => {
  if (!uri) return;

  const u = new URL(uri);
  if (u.protocol !== 's3:') return;
  const bucket = u.hostname;
  const prefix = u.pathname.replace(/^\/+|\/+$/g, '');
  if (!prefix) return;
  return { bucket, prefix };
};

type CreateCommercialAnalysisProps = {
  isOpen: boolean;
  toggleModal: () => void;
};
const CreateCommercialAnalysisModal: FC<
  CreateCommercialAnalysisProps
> = props => {
  const { isOpen, toggleModal } = props;

  const { t } = useTranslation();

  const { Form, methods } = useRHFForm({
    schema: createCommercialAnalysisSchema,
    defaultValues: {
      forceRun: false,
      sequencingTypeId: '',
      allowPartialRun: false,
      dateOfCreation: dayjs(),
    },
  });

  const { channels } = useGetChannels();
  const allowedChannelIds = channels?.reduce((acc: string[], channel) => {
    if (
      performCommercialVisibleChannels.includes(channel.name as ChannelName)
    ) {
      acc.push(channel.id);
    }
    return acc;
  }, []);

  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectEntity>();

  const projectInfiniteQueryResult = useGetInfiniteProjects({
    params: {
      order: 'ASC',
      q: projectSearch,
      orderField: 'name',
      channelIds: allowedChannelIds,
      isPerformAnalysisBatch: true,
      sequencingType: BiobakeryPipeline.SIXTEEN_S,
    },
  });
  const {
    flatData: projects,
    isFetching: isFetchingProject,
    isLoading: isLoadingProject,
  } = projectInfiniteQueryResult;

  useEffect(() => {
    if (
      !isFetchingProject &&
      !isLoadingProject &&
      isOpen &&
      !methods.getValues('projectId')
    ) {
      methods.setValue('projectId', projects?.[0]?.id ?? '');
      setSelectedProject(projects?.[0]);
    }
  }, [isFetchingProject, isLoadingProject, isOpen, methods, projects]);

  const fields: OmitControlControlledInputProps<
    z.infer<typeof createCommercialAnalysisSchema>
  >[] = [
    {
      name: 'dateOfCreation',
      inputType: 'datePicker',
      label: t('extraction.dateOfCreation'),
    },
    {
      showSearch: true,
      name: 'projectId',
      inputType: 'select',
      label: t('titles.project'),
      loading: isLoadingProject || isFetchingProject,
      filterOption: handleFilterOption,
      onSearch: handleSearchSelect(setProjectSearch),
      onPopupScroll: handleScrollPopupToGetNextPage(projectInfiniteQueryResult),
      options: transformInfiniteQueryToSelectOptions(
        projectInfiniteQueryResult,
      ),
      onChange: value => {
        const findProject = projects.find(p => p.id === value);
        setSelectedProject(findProject);
      },
    },
  ];

  const handleCancel = () => {
    toggleModal();
    methods.reset({
      projectId: '',
      forceRun: false,
      sequencingTypeId: '',
      allowPartialRun: false,
      dateOfCreation: dayjs(),
    });
  };

  const {
    mutate: mutateCreateCommercial,
    isPending: isPendingCreateCommercial,
  } = useCreateCommercialAnalysisBatch({
    onSuccess: () => {
      notification.success({ message: t('common.createSuccessful') });
      handleCancel();
      setSelectedProject(undefined);
    },
  });

  const handleSubmit = (
    values: z.infer<typeof createCommercialAnalysisSchema>,
  ) => {
    const s3Bucket = parseS3UriWithURL(
      selectedProject?.analysisBucketPath ?? '',
    );
    const payload: AnalysisBatchCreateDto = {
      ...values,
      dateOfCreation: dayjs(values.dateOfCreation).format(),
      analysisBucketPath: s3Bucket?.prefix,
      analysisBatchSyncBucket: s3Bucket?.bucket,
    };

    return mutateCreateCommercial(payload);
  };

  return (
    <CustomModal
      footer={null}
      open={isOpen}
      onCancel={handleCancel}
      title={t('analysis.addNewCommercialAnalysisBatch')}
    >
      <Form onSubmit={handleSubmit} className="commercial-analysis-form">
        <InputsMapper fields={fields} />

        <CustomButton
          size="large"
          width="100%"
          htmlType="submit"
          loading={isPendingCreateCommercial}
          className="submit-btn"
        >
          {t('extraction.createAndProceed')}
        </CustomButton>
      </Form>
    </CustomModal>
  );
};

export default memo(CreateCommercialAnalysisModal);
